import React, { useState, useEffect } from "react";
import styled from "styled-components";
import useEscape from "../../../hooks/useEscape";
import User from "../../../models/User";
import { AppIconType } from "../AppIcon/AppIcon";
import AppInput, { AppInputProps } from "../AppInput/AppInput";
import AppText from "../AppText/AppText";
import LoadingIndicator from "../../Shared/LoadingIndicator/LoadingIndicator";
import { apiStore } from "../../../stores/api.store";

// TODO: Refactor so this uses AutoComplete component as base /JS
export interface UserAutoCompleteProps
	extends Omit<AppInputProps, "value" | "onChange" | "onChangeText" | "width"> {
	type: "all" | "consultants" | "customer-users" | "internal-users";

	/**
	 * Minimum search string length needed to start doing searching, invoking BE.
	 */
	minChars?: number;

	suggestions: User[];

	onSearch: (search: string | null) => void;

	width?: number;

	onChange?: (val: User | null, index?: number) => void;

	value: User | null;

	renderSuggestion?: (suggestion: User) => React.ReactNode;

	icon?: AppIconType;

	loading?: boolean;
}

const UserAutoComplete: React.FC<UserAutoCompleteProps> = ({
	type,
	suggestions,
	minChars = 2,
	width = 250,
	onSearch,
	onChange,
	value,
	renderSuggestion,
	icon = "search",
	loading = false,
	...inputProps
}) => {
	const [searchStr, setSearchStr] = useState("");

	function handleOnChange(search: string) {
		search = search.trimLeft();
		setSearchStr(search);

		if (search.length >= minChars) {
			onSearch(search);
		} else {
			onChange && onChange(null);
		}
	}

	function handleSelect(index: number) {
		const selectedSuggestion = suggestions[index];

		if (selectedSuggestion) {
			onChange && onChange(selectedSuggestion, index);
			setSearchStr(selectedSuggestion.firstName + " " + selectedSuggestion.lastName);
		} else {
			onChange && onChange(null);
		}
	}

	useEffect(() => {
		if (value) {
			value.firstName && value.lastName && setSearchStr(value.firstName + " " + value.lastName);
		} else {
			setSearchStr("");
		}
	}, [value]);

	useEscape({
		onEscape: () => {
			setSearchStr("");
			handleOnChange("");
		},
	});

	return (
		<UserAutoCompleteStyles width={width}>
			<AppInput
				{...inputProps}
				onChangeText={handleOnChange}
				value={searchStr}
				containerMargin="0"
				icon={icon}
				onEnter={() => handleSelect(0)}
				onFocus={() => handleOnChange(searchStr)}
			/>
			{loading && <LoadingIndicator className="loader" size={16} />}
			{suggestions.length > 0 && (
				<ul className="suggestions">
					{suggestions.map((sugg, i) => (
						<li key={sugg.id} onClick={() => handleSelect(i)}>
							{renderSuggestion ? (
								renderSuggestion(sugg)
							) : (
								<AppText>
									{sugg.firstName} {sugg.lastName}
								</AppText>
							)}
						</li>
					))}
				</ul>
			)}
		</UserAutoCompleteStyles>
	);
};

export default UserAutoComplete;

const UserAutoCompleteStyles = styled.div<{ width: number }>`
	position: relative;
	width: ${(props) => props.width}px;

	.loader {
		position: absolute;
		z-index: 10;
	}

	.suggestions {
		position: absolute;
		background: white;
		list-style: none;
		width: ${(props) => props.width}px;
		z-index: 10;
		border: 1px solid ${(props) => props.theme.colors.COLOR_DIVIDER_GRAY};

		li {
			padding: 0.75rem 0.5rem;
			display: block;
			cursor: pointer;
			border-bottom: 1px solid ${(props) => props.theme.colors.COLOR_DIVIDER_GRAY};

			&:last-child {
				border-bottom-style: none;
			}

			&:hover {
				background-color: ${(props) => props.theme.colors.COLOR_LIGHT_BLUE2};
			}

			&:first-child {
				font-weight: 700;
			}
		}
	}
`;

export const ConsultantAutoComplete: React.FC<{
	value: User | null;
	onChange: (user: User | null) => void;
}> = ({ value, onChange }) => {
	const [suggestions, setSuggestions] = useState([]);
	const [term, setTerm] = useState<null | string>("");
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		async function search() {
			if (term) {
				setLoading(true);
				const { data } = await apiStore.get({
					path: "/consultant-account",
					query: { keyword: term },
					isAuthRequired: true,
				});

				setSuggestions(data.users);
				setLoading(false);
			}
		}
		const searchTimer = setTimeout(() => {
			search();
		}, 400);

		return () => {
			clearTimeout(searchTimer);
			setSuggestions([]);
			setLoading(false);
		};
	}, [term, apiStore]);

	useEffect(() => {
		setSuggestions([]);
	}, [value]);

	return (
		<UserAutoComplete
			type="consultants"
			suggestions={suggestions}
			onSearch={setTerm}
			onChange={onChange}
			value={value}
			loading={loading}
			placeholder="consultant.autoComplete.placeholder"
			renderSuggestion={(sugg) => (
				<AppText>
					{sugg.firstName} {sugg.lastName} {sugg.profile.employeeNumber || ""}
				</AppText>
			)}
		/>
	);
};

export const CustomerAccountAutoComplete: React.FC<{
	value: User | null;
	onChange: (user: User | null) => void;
	placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
	const [suggestions, setSuggestions] = useState([]);
	const [term, setTerm] = useState<null | string>("");
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		async function search() {
			if (term) {
				setLoading(true);
				const { data } = await apiStore.get({
					path: "/customer-account",
					query: { keyword: term },
					isAuthRequired: true,
				});

				setSuggestions(data.users);
				setLoading(false);
			}
		}

		const searchTimer = setTimeout(() => {
			search();
		}, 400);

		return () => {
			clearTimeout(searchTimer);
			setSuggestions([]);
			setLoading(false);
		};
	}, [term, apiStore]);

	useEffect(() => {
		setSuggestions([]);
	}, [value]);

	return (
		<UserAutoComplete
			type="customer-users"
			suggestions={suggestions}
			onSearch={setTerm}
			onChange={onChange}
			value={value}
			width={320}
			loading={loading}
			icon="add-user"
			placeholder={placeholder || "customers.user.autoComplete.placeholder"}
			renderSuggestion={(sugg) => (
				<div>
					<AppText>
						{sugg.firstName} {sugg.lastName} ({sugg.profile.title || "-"})
					</AppText>
					<AppText color="grayText">{sugg.email}</AppText>
				</div>
			)}
		/>
	);
};
